import { useMemo } from "react";

/**
 * Extract the underlying ticker prefix from an Alpaca option symbol.
 * e.g. "SPY260804P00760000" -> "SPY"
 *      "QQQ260804C00400000" -> "QQQ"
 *      "AAPL" -> "AAPL"
 */
function normalizeStrategy(strat) {
    if (!strat) return "NO_STRATEGY";
    const s = String(strat).trim().toUpperCase();
    if (s === "NO_STRATEGY" || s === "N/A" || s === "NONE" || s === "" || s === "SIMPLE" || s === "N/A STRATEGY") {
        return "NO_STRATEGY";
    }
    return s;
}

function getAlpacaUnderlying(symbol) {
    if (!symbol) return null;
    const s = symbol.toUpperCase().trim();
    const match = s.match(/^([A-Z]+)\d/);
    if (match) return match[1];
    return s;
}

export function useAlpacaPositionsView(positions, orders, users) {
    return useMemo(() => {
        const alpacaUsers = (users || []).filter(u => u && String(u.broker).toLowerCase() === "alpaca");
        const alpacaUserIds = new Set(alpacaUsers.map(u => String(u.client_id).toLowerCase()));

        const alpacaPositions = (positions || []).filter(p => p && (
            String(p.broker).toLowerCase() === "alpaca" ||
            alpacaUserIds.has(String(p.clientId || p.client_id).toLowerCase())
        ));
        const alpacaOrders = (orders || []).filter(o => o && (
            String(o.broker).toLowerCase() === "alpaca" ||
            alpacaUserIds.has(String(o.clientId || o.client_id).toLowerCase())
        ));

        // 1. Group positions by Client + Underlying Asset
        const clientAssetMap = {};
        alpacaUsers.forEach(u => {
            clientAssetMap[u.client_id] = {};
        });

        const activeAssetsSet = new Set();

        alpacaPositions.forEach(pos => {
            const clientId = pos.clientId || pos.client_id;
            if (!clientId) return;

            if (!clientAssetMap[clientId]) clientAssetMap[clientId] = {};

            const asset = getAlpacaUnderlying(pos.symbol);
            if (!asset) return;

            activeAssetsSet.add(asset);

            const netQty = parseFloat(pos.netQty ?? pos.net_qty ?? pos.qty ?? 0);
            const pl = parseFloat(pos.pl ?? pos.pnl ?? 0);

            if (!clientAssetMap[clientId][asset]) {
                clientAssetMap[clientId][asset] = {
                    qty: 0,
                    pnl: 0,
                    longQty: 0,
                    shortQty: 0,
                    slQty: 0,
                    slBuyQty: 0,
                    slSellQty: 0,
                    netSignedQty: 0,
                    positions: []
                };
            }

            const item = clientAssetMap[clientId][asset];
            item.qty += Math.abs(netQty);
            item.pnl += pl;
            item.netSignedQty += netQty;
            if (netQty > 0) item.longQty += netQty;
            if (netQty < 0) item.shortQty += Math.abs(netQty);
            item.positions.push(pos);
        });

        // 2. Identify SL orders
        const clientSymbolNetQty = {};
        alpacaPositions.forEach(pos => {
            const clientId = pos.clientId || pos.client_id;
            if (!clientId) return;
            const sym = (pos.symbol || "").toUpperCase().trim();
            const netQty = parseFloat(pos.netQty ?? pos.net_qty ?? pos.qty ?? 0);
            const key = `${clientId}_${sym}`;
            clientSymbolNetQty[key] = (clientSymbolNetQty[key] || 0) + netQty;
        });

        alpacaOrders.forEach(o => {
            const clientId = o.clientId || o.client_id;
            if (!clientId || !clientAssetMap[clientId]) return;

            const asset = getAlpacaUnderlying(o.symbol);
            if (!asset) return;

            const statusStr = String(o.status || "").toLowerCase();
            const statusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;
            const isPending = [
                "new", "partially_filled", "accepted", "pending_new",
                "held", "pending_replace", "pending", "open", "not sent"
            ].includes(statusStr) || statusNo === 6;
            if (!isPending) return;

            const sideStr = String(o.side || "").toLowerCase();
            const isBuy = sideStr === "buy" || sideStr === "1";
            const isSell = sideStr === "sell" || sideStr === "2" || sideStr === "-1";

            const sym = (o.symbol || "").toUpperCase().trim();
            const posNetQty = clientSymbolNetQty[`${clientId}_${sym}`] ?? 0;
            const isClosingLeg = (posNetQty > 0 && isSell) || (posNetQty < 0 && isBuy);
            if (!isClosingLeg) return;

            const item = clientAssetMap[clientId][asset];
            if (item) {
                const qty = Math.abs(parseFloat(o.qty || 0));
                if (isBuy) item.slBuyQty += qty;
                if (isSell) item.slSellQty += qty;
            }
        });

        // 3. Build Row Data
        const data = [];
        Object.entries(clientAssetMap).forEach(([clientId, assets]) => {
            const user = alpacaUsers.find(u => u.client_id === clientId);
            const multiplier = user ? parseFloat(user.multiplier || 1) : 1;

            const rowData = {
                clientId,
                clientName: user ? (user.name || clientId) : `Client ${clientId}`,
                status: user ? user.status : "unknown",
                broker: "alpaca",
                multiplier
            };

            let hasAnyPositions = false;
            activeAssetsSet.forEach(asset => {
                const assetData = assets[asset];
                if (assetData && assetData.qty > 0) {
                    hasAnyPositions = true;
                    if (assetData.netSignedQty > 0) {
                        assetData.slQty = assetData.slSellQty;
                    } else if (assetData.netSignedQty < 0) {
                        assetData.slQty = assetData.slBuyQty;
                    } else {
                        assetData.slQty = 0;
                    }

                    const color = assetData.slQty > 0 && assetData.slQty === assetData.qty ? "green" : "red";

                    rowData[asset] = {
                        multiplier,
                        qty: assetData.qty,
                        slQty: assetData.slQty,
                        pnl: assetData.pnl,
                        netSignedQty: assetData.netSignedQty,
                        longQty: assetData.longQty,
                        shortQty: assetData.shortQty,
                        slBuyQty: assetData.slBuyQty,
                        slSellQty: assetData.slSellQty,
                        positions: assetData.positions,
                        color
                    };
                } else {
                    rowData[asset] = null;
                }
            });

            if (hasAnyPositions) {
                data.push(rowData);
            }
        });

        const activeAssets = Array.from(activeAssetsSet).sort();

        // 4. Calculate symbolStrategyBaseQty
        const symbolStrategyBaseQty = {};
        const positionsByLookupKey = {};

        alpacaPositions.forEach(p => {
            const strategy = normalizeStrategy(p.strategy);
            const sym = (p.symbol || "").toUpperCase().trim();
            const lookupKey = `${sym}_${strategy}`;

            if (!positionsByLookupKey[lookupKey]) {
                positionsByLookupKey[lookupKey] = [];
            }
            positionsByLookupKey[lookupKey].push(p);
        });

        Object.entries(positionsByLookupKey).forEach(([lookupKey, groupPositions]) => {
            const clientQtys = {};
            const clientIdsSet = new Set();
            let symbol = "";
            let strategy = "";

            groupPositions.forEach(p => {
                const clientId = p.clientId || p.client_id;
                const netQty = Math.abs(parseFloat(p.netQty ?? p.net_qty ?? p.qty ?? 0));
                clientQtys[clientId] = (clientQtys[clientId] || 0) + netQty;
                clientIdsSet.add(clientId);
                if (!symbol) symbol = p.symbol;
                if (!strategy || strategy === "N/A") strategy = normalizeStrategy(p.strategy);
            });

            // Normalize and get mode
            const normalizedQtys = [];
            clientIdsSet.forEach(cid => {
                const user = alpacaUsers.find(u => u.client_id === cid);
                const multiplier = user ? parseFloat(user.multiplier || 1) : 1;
                const actQty = clientQtys[cid] || 0;
                normalizedQtys.push(actQty / (multiplier > 0 ? multiplier : 1));
            });

            let baseQty = 0;
            if (normalizedQtys.length > 0) {
                const freq = {};
                let maxFreq = 0;
                let mode = normalizedQtys[0];
                normalizedQtys.forEach(n => {
                    freq[n] = (freq[n] || 0) + 1;
                    if (freq[n] > maxFreq) { maxFreq = freq[n]; mode = n; }
                });
                baseQty = mode;
            }

            symbolStrategyBaseQty[lookupKey] = {
                symbol,
                strategy,
                baseQty,
                index: getAlpacaUnderlying(symbol),
                clients: Array.from(clientIdsSet)
            };
        });

        // 5. Base Qty Map (mode of non-zero normalized quantities)
        const baseQtyMap = {};
        activeAssets.forEach(asset => {
            const qtys = [];
            data.forEach(row => {
                const cell = row[asset];
                if (cell && cell.qty > 0) {
                    const m = cell.multiplier > 0 ? cell.multiplier : 1;
                    qtys.push(cell.qty / m);
                }
            });
            if (qtys.length > 0) {
                const freq = {};
                let maxFreq = 0, mode = qtys[0];
                qtys.forEach(n => {
                    freq[n] = (freq[n] || 0) + 1;
                    if (freq[n] > maxFreq) { maxFreq = freq[n]; mode = n; }
                });
                baseQtyMap[asset] = mode;
            }
        });

        return { data, baseQtyMap, activeAssets, symbolStrategyBaseQty };
    }, [positions, orders, users]);
}
